import React, { useEffect, useState, useMemo } from "react";
import { Search, RefreshCw, AlertCircle, Play, BarChart2, Columns, X, HelpCircle } from "lucide-react";
import apiClient from "../utils/apiClient";

export default function GapPlaybookView({ clientData, isModal, onClose }) {
  const [data, setData] = useState({ optimus: [], pledgeplus: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeSubTab, setActiveSubTab] = useState("optimus"); // "optimus" or "pledgeplus"
  const [searchQuery, setSearchQuery] = useState("");
  const [showDetailed, setShowDetailed] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);

  const fetchResults = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get("gap-playbook/results");
      if (response.data && response.data.status === "success") {
        setData(response.data.data || { optimus: [], pledgeplus: [] });
      } else {
        setError(response.data?.message || "Failed to load scan results.");
      }
    } catch (err) {
      console.error("Error fetching gap playbook results:", err);
      setError(err.response?.data?.detail || err.message || "An error occurred.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchResults();
  }, []);

  const activeRows = useMemo(() => {
    const rawRows = data[activeSubTab] || [];
    if (activeSubTab === "optimus") {
      return rawRows.filter((r) => {
        const sym = (r.symbol || "").trim().toUpperCase();
        return sym.endsWith("CE") || sym.endsWith("PE");
      });
    } else if (activeSubTab === "pledgeplus") {
      return rawRows.filter((r) => {
        const sym = (r.symbol || "").trim().toUpperCase();
        return sym.endsWith("FUT");
      });
    }
    return rawRows;
  }, [data, activeSubTab]);

  const filteredRows = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    if (!q) return activeRows;
    return activeRows.filter((r) => {
      return (
        (r.symbol || "").toLowerCase().includes(q) ||
        (r.zone || "").toLowerCase().includes(q) ||
        (r.action || "").toLowerCase().includes(q) ||
        (r.favor || "").toLowerCase().includes(q)
      );
    });
  }, [activeRows, searchQuery]);

  const getAssetCategory = (symbol) => {
    const sym = (symbol || "").trim().toUpperCase();
    if (sym.includes("BANKNIFTY")) return "BANKNIFTY";
    if (sym.includes("MIDCPNIFTY")) return "MIDCPNIFTY";
    if (sym.includes("FINNIFTY")) return "FINNIFTY";
    if (sym.includes("NIFTY")) return "NIFTY";
    if (sym.includes("SENSEX")) return "SENSEX";
    if (sym.includes("CRUDEOIL") || sym.includes("CRUDE")) return "CRUDEOIL";
    if (sym.includes("GOLD")) return "GOLD";
    return "OTHERS";
  };

  const CATEGORY_ORDER = ["NIFTY", "BANKNIFTY", "SENSEX", "MIDCPNIFTY", "FINNIFTY", "CRUDEOIL", "GOLD", "OTHERS"];

  const groupedRows = useMemo(() => {
    const groups = {};
    filteredRows.forEach((row) => {
      const category = getAssetCategory(row.symbol);
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(row);
    });

    const sortedCategories = Object.keys(groups).sort((a, b) => {
      let idxA = CATEGORY_ORDER.indexOf(a);
      let idxB = CATEGORY_ORDER.indexOf(b);
      if (idxA === -1) idxA = 999;
      if (idxB === -1) idxB = 999;
      return idxA - idxB;
    });

    return sortedCategories.map((cat) => ({
      category: cat,
      rows: groups[cat],
    }));
  }, [filteredRows]);

  return (
    <div className={`flex flex-col h-full w-full font-sans ${isModal ? "" : "bg-[#1a1d29] rounded-xl border border-gray-800 shadow-2xl overflow-hidden"}`}>
      {/* HEADER */}
      <div className={`shrink-0 p-4 md:p-6 ${onClose ? "pr-16 md:pr-20" : ""} border-b border-gray-800 bg-gradient-to-r from-[#1a1d29] to-[#151720] relative`}>
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h2 className="text-lg md:text-xl font-bold text-white flex items-center gap-3">
              <div className="bg-blue-500/10 p-2 rounded-lg border border-blue-500/20">
                <BarChart2 className="w-5 h-5 text-blue-500" />
              </div>
              Daily Gap Scanner
              <span className="ml-2 bg-gray-800 text-gray-300 text-xs px-2.5 py-1 rounded-full border border-gray-700 font-mono">
                {filteredRows.length} Active Gaps
              </span>
            </h2>
          </div>

          <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
            {/* Sub-tab switcher */}
            <div className="flex bg-[#0f1116] p-1 rounded-lg border border-gray-700">
              <button
                onClick={() => setActiveSubTab("optimus")}
                className={`px-4 py-1.5 text-xs font-bold rounded-md transition-all ${
                  activeSubTab === "optimus"
                    ? "bg-blue-600 text-white shadow-md"
                    : "text-gray-400 hover:text-gray-200"
                }`}
              >
                Optimus Gaps
              </button>
              <button
                onClick={() => setActiveSubTab("pledgeplus")}
                className={`px-4 py-1.5 text-xs font-bold rounded-md transition-all ${
                  activeSubTab === "pledgeplus"
                    ? "bg-blue-600 text-white shadow-md"
                    : "text-gray-400 hover:text-gray-200"
                }`}
              >
                Pledge+ Gaps
              </button>
            </div>

            {/* View Mode Toggle Button */}
            <button
              onClick={() => setShowDetailed(!showDetailed)}
              title={showDetailed ? "Switch to Simple View" : "Switch to Detailed View"}
              className={`flex items-center gap-2 px-3 py-2 text-xs font-bold rounded-lg border transition-all ${
                showDetailed
                  ? "bg-amber-500/10 hover:bg-amber-500/20 text-amber-400 border-amber-500/30"
                  : "bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 border-blue-500/30"
              }`}
            >
              <Columns className="w-3.5 h-3.5" />
              <span>{showDetailed ? "Simple View" : "Detailed View"}</span>
            </button>

            {/* Refresh Button */}
            <button
              onClick={fetchResults}
              disabled={loading}
              title="Refresh Data"
              className="flex items-center justify-center p-2.5 bg-[#1f2937] hover:bg-[#374151] text-gray-300 hover:text-white rounded-lg border border-gray-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </button>

            {/* Terminology / Help Button */}
            <button
              onClick={() => setShowHelpModal(true)}
              title="Show Terminology & Help"
              className="flex items-center justify-center p-2.5 bg-[#1f2937] hover:bg-[#374151] text-gray-300 hover:text-white rounded-lg border border-gray-700 transition-all"
            >
              <HelpCircle className="w-4 h-4" />
            </button>

            {/* Search Input */}
            <div className="relative group flex-1 md:w-auto">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search symbol, zone, action..."
                className="bg-[#0f1116] text-gray-200 pl-10 pr-4 py-2 rounded-lg border border-gray-700 focus:border-blue-500 focus:ring-1 w-full md:w-64 text-xs font-medium"
              />
            </div>
          </div>
        </div>

        {/* Close Button */}
        {onClose && (
          <button
            onClick={onClose}
            title="Close"
            className="absolute top-4 right-4 md:top-6 md:right-6 flex items-center justify-center p-2.5 bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-white rounded-lg border border-gray-700 transition-all shadow-md z-10"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* ERROR MESSAGE */}
      {error && (
        <div className="m-4 p-4 bg-red-500/10 border border-red-500/20 rounded-lg flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
          <div>
            <h4 className="text-red-400 font-bold text-sm">Failed to Load Scan Results</h4>
            <p className="text-xs text-gray-400 mt-1">{error}</p>
          </div>
        </div>
      )}

      {/* TABLE */}
      <style>{`
        .sleek-scrollbar::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        .sleek-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .sleek-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(59, 130, 246, 0.3);
          border-radius: 10px;
        }
        .sleek-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(59, 130, 246, 0.5);
        }
      `}</style>
      <div className="flex-1 relative min-h-0 z-[1]">
        <div className="absolute inset-0 overflow-auto sleek-scrollbar">
          {loading && (
            <div className="absolute inset-0 bg-[#1a1d29]/80 backdrop-blur-sm z-50 flex flex-col justify-center items-center">
              <RefreshCw className="w-10 h-10 text-blue-500 animate-spin mb-4" />
              <span className="text-gray-400 animate-pulse font-medium text-sm">
                Fetching latest gap scanner data...
              </span>
            </div>
          )}

          <table className={`w-full text-xs transition-all duration-300 ${showDetailed ? "min-w-[1500px]" : "min-w-full"}`}>
            <thead className="bg-[#14161f] text-gray-400 text-xs font-semibold uppercase tracking-wider sticky top-0 z-20 border-b border-gray-800">
              {showDetailed ? (
                <tr>
                  <th className="py-3.5 px-4 text-left whitespace-nowrap">Symbol</th>
                  <th className="py-3.5 px-4 text-left whitespace-nowrap">Ref Symbol</th>
                  <th className="py-3.5 px-4 text-center whitespace-nowrap">Side</th>
                  <th className="py-3.5 px-4 text-right whitespace-nowrap">Net Qty</th>
                  <th className="py-3.5 px-4 text-right whitespace-nowrap">Avg Price</th>
                  <th className="py-3.5 px-4 text-right whitespace-nowrap">Fut Prev Close</th>
                  <th className="py-3.5 px-4 text-right whitespace-nowrap">Fut Open</th>
                  <th className="py-3.5 px-4 text-right whitespace-nowrap">Opt Prev Close</th>
                  <th className="py-3.5 px-4 text-right whitespace-nowrap">Opt Open</th>
                  <th className="py-3.5 px-4 text-center whitespace-nowrap">Open Time</th>
                  <th className="py-3.5 px-4 text-right whitespace-nowrap">ATR20 (abs)</th>
                  <th className="py-3.5 px-4 text-right whitespace-nowrap">Gap %</th>
                  <th className="py-3.5 px-4 text-right whitespace-nowrap">ATR %</th>
                  <th className="py-3.5 px-4 text-right whitespace-nowrap">Ratio</th>
                  <th className="py-3.5 px-4 text-center whitespace-nowrap">Favor</th>
                  <th className="py-3.5 px-4 text-center whitespace-nowrap">Zone</th>
                  <th className="py-3.5 px-4 text-left min-w-[400px]">Action</th>
                  <th className="py-3.5 px-4 text-center whitespace-nowrap">As Of (prev)</th>
                </tr>
              ) : (
                <tr>
                  <th className="py-3.5 px-4 text-left w-1/4 whitespace-nowrap">Symbol</th>
                  <th className="py-3.5 px-4 text-right w-1/4 whitespace-nowrap">FUT Gap %</th>
                  <th className="py-3.5 px-4 text-left w-2/4 whitespace-nowrap">Action</th>
                </tr>
              )}
            </thead>

            <tbody className="divide-y divide-gray-800/40">
              {groupedRows.length === 0 ? (
                <tr>
                  <td colSpan={showDetailed ? 18 : 3} className="text-center py-20 bg-[#1a1d29]">
                    <BarChart2 className="w-16 h-16 mb-4 opacity-10 mx-auto text-gray-400" />
                    <p className="text-gray-500 text-sm font-semibold">No gap scanner results found</p>
                    <p className="text-gray-600 text-xs mt-1">Run scanner scripts at market open to generate data</p>
                  </td>
                </tr>
              ) : (
                groupedRows.map(({ category, rows }) => (
                  <React.Fragment key={category}>
                    {/* Category Divider Row */}
                    <tr className="bg-[#14161f]/95 sticky z-10 font-bold border-y border-gray-800/80">
                      <td colSpan={showDetailed ? 18 : 3} className="py-2.5 px-4 text-left">
                        <span className="text-xs uppercase tracking-wider text-blue-400 font-extrabold">{category}</span>
                        <span className="ml-2 text-[10px] text-gray-500 font-medium">({rows.length} {rows.length === 1 ? 'position' : 'positions'})</span>
                      </td>
                    </tr>
                    {rows.map((row, idx) => {
                      const sideStyle = row.side === "LONG" ? "text-green-400 bg-green-500/10 border-green-500/20" : "text-red-400 bg-red-500/10 border-red-500/20";
                      
                      let favorText = "—";
                      let favorStyle = "text-gray-500";
                      if (row.favor === "IN_FAVOR") {
                        favorText = "In Favour";
                        favorStyle = "text-green-400 bg-green-500/10 border-green-500/20";
                      } else if (row.favor === "AGAINST_FAVOR") {
                        favorText = "Against Favour";
                        favorStyle = "text-red-400 bg-red-500/10 border-red-500/20";
                      }

                      const gapVal = parseFloat(row.gap_pct);
                      const gapColor = gapVal >= 0 ? "text-green-400" : "text-red-400";
                      
                      const ratioVal = parseFloat(row.ratio);
                      const isHighRatio = !isNaN(ratioVal) && Math.abs(ratioVal) > 1.0;
                      const ratioDisplay = isHighRatio ? `text-yellow-400 font-bold border border-yellow-500/30 bg-yellow-500/5 px-1.5 py-0.5 rounded` : "text-gray-300";

                      if (showDetailed) {
                        return (
                          <tr key={idx} className="hover:bg-[#1e212d] transition-colors border-b border-gray-800/30">
                            {/* Symbol */}
                            <td className="py-3 px-4 font-bold text-white text-xs whitespace-nowrap">{row.symbol}</td>
                            
                            {/* Ref Symbol */}
                            <td className="py-3 px-4 text-gray-400 font-mono whitespace-nowrap">{row.atr_sym !== row.symbol ? row.atr_sym : "—"}</td>
                            
                            {/* Side */}
                            <td className="py-3 px-4 text-center whitespace-nowrap">
                              <span className={`px-2 py-0.5 rounded font-black border text-[10px] uppercase ${sideStyle}`}>
                                {row.side}
                              </span>
                            </td>

                            {/* Net Qty */}
                            <td className="py-3 px-4 text-right font-mono text-gray-300 font-bold whitespace-nowrap">{row.net_qty}</td>
                            
                            {/* Avg Price */}
                            <td className="py-3 px-4 text-right font-mono text-gray-400 whitespace-nowrap">{row.avg_price ?? "—"}</td>
                            
                            {/* Fut Prev Close */}
                            <td className="py-3 px-4 text-right font-mono text-gray-400 whitespace-nowrap">{row.prev_close ?? "—"}</td>
                            
                            {/* Fut Open */}
                            <td className="py-3 px-4 text-right font-mono text-blue-400 whitespace-nowrap">{row.today_open ?? "—"}</td>
                            
                            {/* Opt Prev Close */}
                            <td className="py-3 px-4 text-right font-mono text-gray-400 whitespace-nowrap">{row.opt_prev_close ?? "—"}</td>
                            
                            {/* Opt Open */}
                            <td className="py-3 px-4 text-right font-mono text-blue-400 whitespace-nowrap">{row.opt_today_open ?? "—"}</td>
                            
                            {/* Open Time */}
                            <td className="py-3 px-4 text-center text-gray-500 whitespace-nowrap">{row.session_open ? row.session_open.split(" (")[0] : "—"}</td>
                            
                            {/* ATR20 (abs) */}
                            <td className="py-3 px-4 text-right font-mono text-purple-400 whitespace-nowrap">{row.atr20 ?? "—"}</td>
                            
                            {/* Gap % */}
                            <td className="py-3 px-4 text-right font-mono font-bold whitespace-nowrap">
                              <span className={gapColor}>{row.gap_pct !== null && row.gap_pct !== undefined ? `${row.gap_pct}%` : "—"}</span>
                            </td>
                            
                            {/* ATR % */}
                            <td className="py-3 px-4 text-right font-mono text-purple-400 whitespace-nowrap">{row.atr20_pct !== null && row.atr20_pct !== undefined ? `${row.atr20_pct}%` : "—"}</td>
                            
                            {/* Ratio */}
                            <td className="py-3 px-4 text-right font-mono font-bold whitespace-nowrap">
                              <span className={ratioDisplay}>{row.ratio ?? "—"}</span>
                            </td>
                            
                            {/* Favor */}
                            <td className="py-3 px-4 text-center whitespace-nowrap">
                              {row.favor !== "—" ? (
                                <span className={`px-2 py-0.5 rounded font-black border text-[10px] uppercase ${favorStyle}`}>
                                  {favorText}
                                </span>
                              ) : "—"}
                            </td>
                            
                            {/* Zone */}
                            <td className="py-3 px-4 text-center whitespace-nowrap">
                              {row.zone !== "—" ? (
                                <span className="px-2 py-0.5 rounded bg-purple-500/10 text-purple-400 border border-purple-500/20 font-black text-[10px]">
                                  {row.zone}
                                </span>
                              ) : "—"}
                            </td>
                            
                            {/* Action */}
                            <td className="py-3 px-4 text-left min-w-[400px] max-w-xl">
                              {row.error ? (
                                <span className="text-red-400 font-semibold block leading-normal whitespace-pre-wrap text-[11px]">
                                  {row.error}
                                </span>
                              ) : row.action && row.action !== "—" ? (
                                <span className="text-yellow-400/90 font-medium block leading-relaxed whitespace-pre-wrap text-[11px]">
                                  {row.action}
                                </span>
                              ) : "—"}
                            </td>
                            
                            {/* As of prev date */}
                            <td className="py-3 px-4 text-center font-mono text-gray-500 whitespace-nowrap">{row.yest_date ?? "—"}</td>
                          </tr>
                        );
                      } else {
                        return (
                          <tr key={idx} className="hover:bg-[#1e212d] transition-colors border-b border-gray-800/30">
                            {/* Symbol */}
                            <td className="py-3 px-4 font-bold text-white text-xs w-1/4">{row.symbol}</td>
                            
                            {/* Gap % */}
                            <td className="py-3 px-4 text-right font-mono font-bold w-1/4">
                              <span className={gapColor}>{row.gap_pct !== null && row.gap_pct !== undefined ? `${row.gap_pct}%` : "—"}</span>
                            </td>
                            
                            {/* Action */}
                            <td className="py-3 px-4 text-left w-2/4">
                              {row.error ? (
                                <span className="text-red-400 font-semibold block leading-normal whitespace-pre-wrap text-[11px]">
                                  {row.error}
                                </span>
                              ) : row.action && row.action !== "—" ? (
                                <span className="text-yellow-400/90 font-medium block leading-relaxed whitespace-pre-wrap text-[11px]">
                                  {row.action}
                                </span>
                              ) : "—"}
                            </td>
                          </tr>
                        );
                      }
                    })}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* TERMINOLOGY HELP MODAL */}
      {showHelpModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999] flex justify-center items-center p-4">
          <div className="bg-[#1a1d29] border border-gray-800 rounded-xl max-w-lg w-full shadow-2xl overflow-hidden font-sans">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-800 bg-[#14161f]">
              <h3 className="text-white font-bold text-sm flex items-center gap-2">
                <HelpCircle className="w-4 h-4 text-blue-500" />
                Playbook Terminology Reference
              </h3>
              <button 
                onClick={() => setShowHelpModal(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-5 space-y-4 text-xs text-gray-300 leading-relaxed max-h-[70vh] overflow-y-auto sleek-scrollbar">
              <div className="space-y-1.5">
                <span className="text-blue-400 font-bold block text-sm">H1 (1-Hour Candle)</span>
                <p className="text-gray-400">
                  Refers to the price action of the first 1 hour of the trading day.
                </p>
                <ul className="list-disc pl-4 text-gray-500 space-y-0.5 mt-1">
                  <li><strong>Commodities (Gold, Silver, Crude):</strong> Closes at <strong>10:00 AM</strong> (1 hour after 9:00 AM open).</li>
                  <li><strong>Equity Indices (Nifty, BankNifty, Sensex, etc.):</strong> Closes at <strong>10:15 AM</strong> (1 hour after 9:15 AM open).</li>
                </ul>
              </div>

              <div className="space-y-1">
                <span className="text-green-400 font-bold block text-sm">H1 Toward (Fill)</span>
                <p className="text-gray-400 font-normal">
                  The first 1-hour candle closed in the direction of the gap fill (yesterday's close price). 
                  For example, if the market gapped down, H1 closed green/bullish.
                </p>
              </div>

              <div className="space-y-1">
                <span className="text-red-400 font-bold block text-sm">H1 Away (from Fill)</span>
                <p className="text-gray-400 font-normal">
                  The first 1-hour candle closed in the opposite direction of the gap fill, continuing the gap breakout direction. 
                  For example, if the market gapped down, H1 closed red/bearish.
                </p>
              </div>

              <div className="space-y-1">
                <span className="text-amber-400 font-bold block text-sm">PC (Previous Close)</span>
                <p className="text-gray-400 font-normal">
                  Yesterday's daily closing price.
                </p>
              </div>

              <div className="space-y-1">
                <span className="text-purple-400 font-bold block text-sm">TP (Take Profit)</span>
                <p className="text-gray-400 font-normal">
                  Target price level where a specified portion of the position is exited to book profits.
                </p>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-4 bg-[#14161f] border-t border-gray-800 flex justify-end">
              <button
                onClick={() => setShowHelpModal(false)}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-bold transition-all shadow-md"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
